import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
} from "@angular/core";
import { MatCardActions } from "@angular/material/card";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from "@angular/material/slide-toggle";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  LoadingDialogComponent,
} from "@cotecna/dialogs";
import { User } from "src/app/session/models/user";
import { StorageService } from "src/app/session/services/storage.service";
import { UserService } from "src/app/session/services/user.service";
import { FeatureFlagsService } from "src/app/shared/services/feature-flags/feature-flags.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import {
  COTECNA_USER_KEY,
  DELETE_SINGLE_PICTURE_MESSAGE,
  DOWNLOAD_SINGLE_PICTURE_MESSAGE,
  SHARE_SINGLE_PICTURE_MESSAGE,
  UNSHARE_SINGLE_PICTURE_MESSAGE,
} from "../../constants/constants";
import { Photo } from "../../model/photo";
import { InspectionDetailsService } from "../../services/inspection-details.service";
import { PhotoCacheService } from "../../services/photo-cache.service";

@Component({
  selector: "im-view-photos-dialog",
  imports: [MatIcon, MatCardActions, MatProgressSpinnerModule, MatSlideToggle],
  templateUrl: "./view-photos-dialog.component.html",
  styleUrl: "./view-photos-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ViewPhotosDialogComponent implements OnInit {
  public selectedPhotoName: string;
  public title: string;
  public minZoom: number = 50;
  public maxZoom: number = 200;
  public zoomLevel: number = 100;
  public isLoading: boolean = false;
  public photos: Photo[] = [];
  private photoColection: Photo[] = [];
  public currentIndex: number = 0;
  public errorMessage: string;
  public imageSrc: string | undefined;
  public photoShared: boolean = false;
  private user: User;
  private inspectionId: string;
  private inspectionName: string;

  public constructor(
    private readonly inspectionDetailsService: InspectionDetailsService,
    private readonly dialogRef: MatDialogRef<ViewPhotosDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly photoCacheService: PhotoCacheService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly userRolePermissionsService: UserRolePermissionsService,
    private readonly userService: UserService,
    private readonly storageService: StorageService,
    private readonly featureFlagsService: FeatureFlagsService,
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: {
      inspectionId: string;
      selectedPhoto: string;
      photos: Photo[];
      inspectionName: string;
    },
  ) {}

  public async ngOnInit() {
    this.user = await this.storageService.get(COTECNA_USER_KEY);
    this.inspectionName = this.dialogData?.inspectionName ?? "";
    this.selectedPhotoName = this.dialogData?.selectedPhoto ?? undefined;
    this.inspectionId = this.dialogData?.inspectionId ?? "";
    this.photos = this.dialogData?.photos ?? [];
    this.currentIndex =
      this.photos.findIndex(
        (photo) => photo.filename === this.selectedPhotoName,
      ) ?? 0;
    this.setTitle();
    if (this.selectedPhotoName) {
      this.loadImage();
      this.isPhotoShared();
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public handleDeleteKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      this.delete();
      event.preventDefault();
    }
  }

  public async delete(): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "delete",
        okButton: {
          text: "delete",
          color: "warn",
        },
        title: "Delete image",
        message: `Do you want to delete the picture?`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        DELETE_SINGLE_PICTURE_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.deletePicture(
          this.inspectionId,
          this.photos[this.currentIndex],
        );
        this.photoCacheService.notifyPhotoDeleted(this.selectedPhotoName);
        await this.photoCacheService.deletePhoto(this.selectedPhotoName);
        localStorage.removeItem(this.selectedPhotoName);

        this.photos = this.photos.filter(
          (pic) => pic.filename != this.selectedPhotoName,
        );

        if (this.photos.length === 0) {
          this.selectedPhotoName = undefined;
          this.imageSrc = undefined;
          this.cdRef.detectChanges();
          this.dialogRef.close();
          return;
        } else {
          if (this.currentIndex >= this.photos.length) {
            this.currentIndex = this.photos.length - 1;
          }
          this.selectedPhotoName = this.photos[this.currentIndex].filename;
          this.setTitle();
          await this.loadImage();
          this.photoShared = this.photos[this.currentIndex].shared;
        }

        this.cdRef.detectChanges();
      } finally {
        loadingDialogRef.close();
      }
    }
  }

  public previous(): void {
    this.errorMessage = "";
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.selectedPhotoName = this.photos[this.currentIndex].filename;
      this.setTitle();
      this.loadImage();
      this.photoShared = this.photos[this.currentIndex].shared;
    }
  }

  public next(): void {
    this.errorMessage = "";
    if (this.currentIndex < this.photos.length - 1) {
      this.currentIndex++;
      this.selectedPhotoName = this.photos[this.currentIndex].filename;
      this.setTitle();
      this.loadImage();
      this.photoShared = this.photos[this.currentIndex].shared;
    }
  }

  public async download(): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "get_app",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Download image",
        message: `Do you want to download the picture?`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        DOWNLOAD_SINGLE_PICTURE_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.downloadPicture(
          this.inspectionId,
          this.inspectionName,
          this.photos[this.currentIndex],
        );
      } finally {
        loadingDialogRef.close();
      }
    }
  }

  public handleDownloadKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      this.download();
      event.preventDefault();
    }
  }

  private setTitle(index?: number): void {
    const safePhotos = this.photos ?? [];
    let photoIndex = index ?? this.currentIndex;
    photoIndex ??= safePhotos.findIndex(
      (doc) => doc.filename === this.selectedPhotoName,
    );

    if (photoIndex === -1 || photoIndex === undefined || photoIndex === null) {
      photoIndex = Math.min(this.currentIndex, safePhotos.length - 1);
    }

    if (safePhotos.length === 0) {
      this.title = "(0/0)";
      return;
    }

    this.title = `(${photoIndex + 1}/${safePhotos.length}) ${safePhotos[photoIndex].filename}`;
  }

  public zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + 10, this.maxZoom);
    }
  }

  public zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel = Math.max(this.zoomLevel - 10, this.minZoom);
    }
  }

  public onSharePhoto(toggleEvent: MatSlideToggleChange): void {
    const currentPhoto = this.dialogData.photos.find(
      (photo) => photo.filename === this.selectedPhotoName,
    );

    (() =>
      toggleEvent.checked
        ? this.sharePhoto(toggleEvent, currentPhoto)
        : this.unsharePhoto(toggleEvent, currentPhoto))();
  }

  public allowSharePictures(): boolean {
    if (this.featureFlagsService.allowSharePictures()) {
      const isUserAllowed = this.userService.shareAllowed(this.user);
      if (isUserAllowed) {
        return true;
      }
    }
    return false;
  }

  public canDownloadOrDeletePictures(): boolean {
    return this.userRolePermissionsService.canDownloadOrDeletePictures();
  }

  private async sharePhoto(toggleEvent: any, photo: Photo): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Share images on external sources",
        message: `Confirm to share the image on external sources with client`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        SHARE_SINGLE_PICTURE_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.sharePicture(
          this.inspectionId,
          photo.filename,
        );
        if (this.photos[this.currentIndex]) {
          this.photos[this.currentIndex].shared = true;
          this.photoShared = this.photos[this.currentIndex].shared;
        }
      } finally {
        loadingDialogRef.close();
      }
    } else {
      toggleEvent.source.checked = false;
    }
  }

  private async unsharePhoto(toggleEvent: any, photo: Photo): Promise<void> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Stop sharing images on external sources",
        message: `Confirm to stop sharing the image on external sources with client`,
      },
      panelClass: "share-document-dialog",
      autoFocus: false,
    });
    const confirmDialogResult = await confirmDialog.afterClosed().toPromise();
    if (confirmDialogResult) {
      const loadingDialogRef = this.openLoadingDialog(
        UNSHARE_SINGLE_PICTURE_MESSAGE,
      );
      try {
        await this.inspectionDetailsService.unsharePicture(
          this.inspectionId,
          photo.filename,
        );
        if (this.photos[this.currentIndex]) {
          this.photos[this.currentIndex].shared = false;
          this.photoShared = this.photos[this.currentIndex].shared;
        }
      } finally {
        loadingDialogRef.close();
      }
    } else {
      toggleEvent.source.checked = true;
    }
  }

  private openLoadingDialog(message: string) {
    return this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: message,
    });
  }

  private async loadImage(): Promise<void> {
    if (!this.selectedPhotoName) {
      this.imageSrc = undefined;
      return;
    }

    try {
      this.isLoading = true;

      const cachedDataUrl = await this.photoCacheService.getPhoto(
        this.selectedPhotoName,
      );
      if (cachedDataUrl) {
        this.imageSrc = cachedDataUrl;
        return;
      }

      const blob = await this.inspectionDetailsService.previewPicture(
        this.inspectionId,
        this.selectedPhotoName,
      );
      if (!blob) {
        this.errorMessage = "An error occurred while loading the image.";
        return;
      }
      const reader = new FileReader();

      reader.onload = async () => {
        const dataUrl = reader.result as string;
        this.imageSrc = dataUrl;
        await this.photoCacheService.setPhoto(this.selectedPhotoName, dataUrl);

        const isPhotoExist = this.photoColection?.find(
          (photo) => photo.filename === this.selectedPhotoName,
        );
        if (!isPhotoExist) {
          if (!this.photoColection) {
            this.photoColection = [];
          }
          this.photoColection.push({
            photo: dataUrl,
            shared: false,
            filename: this.selectedPhotoName,
          });
        }
        this.cdRef.detectChanges();
      };
      reader.readAsDataURL(blob);
    } catch {
      this.errorMessage = "An error occurred while loading the image.";
    } finally {
      this.isLoading = false;
    }
  }

  private async isPhotoShared(): Promise<void> {
    const matchedPhoto = (this.dialogData?.photos ?? []).find(
      (photo) => photo.filename === this.selectedPhotoName,
    );
    this.photoShared = matchedPhoto?.shared ?? false;
  }
}
